import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FormSection } from 'components';
import DocumentGroup from './DocumentGroup';
import EventsSection from './EventsSection';
import GlobalEvents from './GlobalEvents';

/**
 * The four groups a Young Crew yearly report is made of.
 *
 * The Young Crew fills this in and the YC Management Board reads it back, so
 * both screens render the very same component: what the board reviews is what
 * the Young Crew saw, with every input disabled, rather than a second rendering
 * that could drift away from the form over time.
 */
const ReportBody = ({ data, form, errors, readOnly, handlers }) => {
  const { t } = useTranslation();

  const extensions = data.allowedExtensions || {};

  return (
    <>
      <FormSection
        title={t('page.yc.reports.form.yearlyPlan')}
        description={t('page.yc.reports.form.yearlyPlanHelp')}
      >
        <DocumentGroup
          id="yearlyPlan"
          label={t('page.yc.reports.form.documents')}
          required={!readOnly}
          disabled={readOnly}
          error={errors.yearlyPlan}
          extensions={extensions.document || []}
          maxSize={data.maxFileSize}
          stored={form.yearlyPlan}
          pending={form.yearlyPlanNew}
          onAdd={(file) => handlers.addDocument('yearlyPlanNew', file)}
          onRemoveStored={(fileId) => handlers.removeStoredDocument('yearlyPlan', fileId)}
          onRemovePending={(index) => handlers.removePendingDocument('yearlyPlanNew', index)}
        />
      </FormSection>

      <FormSection
        title={t('page.yc.reports.form.yearlyBudget')}
        description={t('page.yc.reports.form.yearlyBudgetHelp')}
      >
        <DocumentGroup
          id="yearlyBudget"
          label={t('page.yc.reports.form.documents')}
          required={!readOnly}
          disabled={readOnly}
          error={errors.yearlyBudget}
          extensions={extensions.document || []}
          maxSize={data.maxFileSize}
          stored={form.yearlyBudget}
          pending={form.yearlyBudgetNew}
          onAdd={(file) => handlers.addDocument('yearlyBudgetNew', file)}
          onRemoveStored={(fileId) => handlers.removeStoredDocument('yearlyBudget', fileId)}
          onRemovePending={(index) => handlers.removePendingDocument('yearlyBudgetNew', index)}
        />
      </FormSection>

      <FormSection
        title={t('page.yc.reports.form.events')}
        description={t('page.yc.reports.form.eventsHelp')}
      >
        <EventsSection
          events={form.events}
          registeredEventOptions={data.registeredEventOptions}
          modeOfDeliveryOptions={data.modeOfDeliveryOptions}
          proofExtensions={extensions.proof || []}
          maxFileSize={data.maxFileSize}
          errors={errors}
          disabled={readOnly}
          onChange={handlers.patchEvent}
          onAdd={handlers.addEvent}
          onRemove={handlers.removeEvent}
          onAddProof={handlers.addProof}
          onRemoveStoredProof={handlers.removeStoredProof}
          onRemovePendingProof={handlers.removePendingProof}
          registerCard={handlers.registerCard}
        />
      </FormSection>

      <FormSection
        title={t('page.yc.reports.form.globalEvents')}
        description={t('page.yc.reports.form.globalEventsHelp')}
      >
        <GlobalEvents
          participated={form.participatedInGlobalEvents}
          selected={form.globalEvents}
          options={data.globalEventOptions}
          onRecord={data.globalEventsOnRecord}
          errors={errors}
          disabled={readOnly}
          onParticipated={handlers.setParticipated}
          onToggle={handlers.setGlobalEvents}
        />
      </FormSection>
    </>
  );
};

ReportBody.propTypes = {
  data: PropTypes.shape({
    allowedExtensions: PropTypes.shape({
      document: PropTypes.arrayOf(PropTypes.string),
      proof: PropTypes.arrayOf(PropTypes.string),
    }),
    maxFileSize: PropTypes.number,
    registeredEventOptions: PropTypes.arrayOf(PropTypes.shape({})),
    modeOfDeliveryOptions: PropTypes.arrayOf(PropTypes.shape({})),
    globalEventOptions: PropTypes.arrayOf(PropTypes.shape({})),
    globalEventsOnRecord: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  form: PropTypes.shape({
    yearlyPlan: PropTypes.arrayOf(PropTypes.shape({})),
    yearlyPlanNew: PropTypes.arrayOf(PropTypes.shape({})),
    yearlyBudget: PropTypes.arrayOf(PropTypes.shape({})),
    yearlyBudgetNew: PropTypes.arrayOf(PropTypes.shape({})),
    events: PropTypes.arrayOf(PropTypes.shape({})),
    participatedInGlobalEvents: PropTypes.bool,
    globalEvents: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  readOnly: PropTypes.bool,
  handlers: PropTypes.objectOf(PropTypes.func),
};

ReportBody.defaultProps = {
  errors: {},
  readOnly: false,
  handlers: {},
};

export default ReportBody;
